import React, { useState, useMemo } from "react";
import { useFormatter } from "./../../../util/formatter";
import LoaderGraph from "../../general/LoaderGraph";
import fonts_config from "./fonts_config.json";
import pdfFonts from "pdfmake/build/vfs_fonts.js";
import pdfMake from "pdfmake/build/pdfmake.js";
import { useSolarCalc } from "../../../util/calculations";
import GYLogo from "../../../images/BASE64/GYLogo";
import moment from "moment-timezone";
import { Button, Row } from "react-bootstrap";
import usePeakLabels from "../../../util/peakLabels";
import rfdc from 'rfdc';

const clone = rfdc();

pdfMake.vfs = pdfFonts.pdfMake.vfs;
pdfMake.fonts = fonts_config;

function InvoiceTemplate_VN({ onDocDefinition = () => { }, ...props }) {
	const [PDF, setPDF] = useState(null);
	const [PDFDoc, setPDFDoc] = useState(null);
	const [invoiceName, setInvoiceName] = useState("");
	const [formatNumber] = useFormatter();
	const solarCalc = useSolarCalc();
	const parseNo = solarCalc.parseFormatNumber;
	const logo = GYLogo;
	const country = props.selectedSite.value.country;
	const _basePeakLabels = usePeakLabels(country);
	const _invoiceDateStr = props.selectedSite.value.dateStr;
	const showSTDRow = moment(_invoiceDateStr, ["MMM-YYYY", "YYYY-MM"]).isSameOrAfter(moment("2027-01", "YYYY-MM"));
	const peakLabels = useMemo(() => {
		return showSTDRow
			? [_basePeakLabels[0], "Off-peak Hours", ..._basePeakLabels.slice(1)] // ON, OFF, Standard peaks
			: _basePeakLabels;
	}, [_basePeakLabels, showSTDRow]);
	const peakLabelsVN = useMemo(() => {
		return showSTDRow
			? [`Giờ cao điểm`, `Giờ thấp điểm`, `Giờ bình thường`]
			: [`Giờ cao điểm`, `Giờ bình thường`];
	}, [showSTDRow]);


	const checkNullValues = (value) => value != null;

	// build extra line in invoice table VERSION 1
	// version 1 is the hard coded version.
	const extraInvoiceTableRow = ({ action, params }) => {
		// default return
		if (action === 'calSubtotal') {
			//sites with extralines
			const { subtotal } = params;
			if (props.selectedSite.value.specialDiscount !== null && props.selectedSite.value.specialComment !== null) {
				const newSubTotal = subtotal + props.selectedSite.value.specialDiscount;
				return newSubTotal;
			} else
				return subtotal; // do nothing with the subtotal
		}
		if (action === 'buildRow') {
			return ["", "", "", ""]; // return empty row by default
		}
	};

	/**
	 * Generate invoice PDF
	 * @param {obj} selectedSite
	 */
	const generateInvoice = async (selectedSite) => {
		const data = selectedSite.value;
		const location = data.location.split("_");
		const billingName = data.billingName != null ? data.billingName : "-";
		const billingAddress = data.billingAddress != null ? data.billingAddress : "-";
		const solarEntity = data.solarEntity;
		const tariffData = data.tariffData;
		const bankAccount = data.bankAccount;
		const hasBankAcc = Object.values(bankAccount).every((value) =>
			checkNullValues(value)
		);

		const invoiceNumber = data.invNo;

		setInvoiceName(invoiceNumber);

		const tariffCategory = tariffData.tariffID.split(" ")[0];
		const tariff = getTariffText(tariffData);
		const evnText = getEVNText(tariffData.EAEng);
		const creditTerm = data.creditTerm;

		// Discount text
		const discountOnText = getDiscountText(data.discountON);
		const discountOffText = getDiscountText(data.discountOFF);
		const discountStdText = getDiscountText(data.discountSTD ?? 0);

		const GYDiscountOnText = getGYDiscountText(data.discountON);
		const GYDiscountOffText = getGYDiscountText(data.discountOFF);
		const GYDiscountStdText = getGYDiscountText(data.discountSTD ?? 0);

		let refDate = moment(data.dateStr).endOf("month");
		let itemDescPeriod = null
		if (tariffData.tariffID == "FIT" && data.moreEVNInputs != null) {
			// finding the latest date of data.moreEVNInputs
			let latestDate = moment(data.moreEVNInputs[0].end_date);
			data.moreEVNInputs.forEach((item, index) => {
				if (moment(item.end_date).isAfter(latestDate)) {
					latestDate = moment(item.end_date);
				}
			});
			refDate = latestDate;
		}

		if (tariffData.tariffID != "FIT" && data.old_price != null) {
			itemDescPeriod = {
				prev: {
					from: moment(data.dateStr).format("DD/MM/YYYY"),
					to: moment(data.old_price?.end_date).format("DD/MM/YYYY"),
				},
				curr: {
					from: moment(data.old_price?.end_date).add(1, "days").format("DD/MM/YYYY"),
					to: moment(data.dateStr).endOf("month").format("DD/MM/YYYY"),
				}
			};
		}

		const isSCFIT = data.contractID.includes("SCFIT");

		const dueDate = moment(refDate).add(creditTerm, "days").format("DD/MM/YYYY");

		// Table item description
		const itemDesc = getItemDesc(itemDescPeriod);

		let EVNInputs = null;
		if (data.EVNInputs != null) {
			EVNInputs = data.EVNInputs;
			if (data.moreEVNInputs != null) {
				const totals = data.moreEVNInputs.reduce((acc, item) => {
					return acc + item.EVN_total;
				}, 0);
				EVNInputs.EVN_total = totals;
				const ons = data.moreEVNInputs.reduce((acc, item) => {
					return acc + item.EVN_on;
				}, 0);
				EVNInputs.EVN_on = ons;
				const offs = data.moreEVNInputs.reduce((acc, item) => {
					return acc + item.EVN_off;
				}, 0);
				EVNInputs.EVN_off = offs;
				const stds = data.moreEVNInputs.reduce((acc, item) => {
					return acc + (item.EVN_std_temp || 0);
				}, 0);
				EVNInputs.EVN_std_temp = (EVNInputs.EVN_std_temp || 0) + stds;
			}
		}

		// Shared revenue - stay the same even for tariff changed in the middle of the month
		let SRParams = null;
		if (EVNInputs != null) {
			const EVNTotal = EVNInputs.EVN_total;
			const FITPrice = data.FITOn;

			SRParams = solarCalc.calculateSharedRevenue(data.contractID, data.discountFT, EVNTotal, FITPrice);
		}
		const SRSubtotal = SRParams != null ? SRParams.subtotal : 0;
		const vatDisplay = parseNo(data.vat * 100);

		// EVN tariff prices for normal case
		let priceObj = getPriceObj(data);

		// Solar production for normal case (always use _temp columns; before 2027-01 they equal non-temp)
		const prodData = {
			solarON: data.solarON_temp ?? data.solarON,
			solarOFF: data.solarOFF_temp ?? data.solarOFF,
			solarSTD_temp: data.solarSTD_temp ?? 0,
			solarTotal: data.solarTotal,
			contractID: data.contractID,
		};
		let prodObj = solarCalc.calculateProd(prodData, EVNInputs);

		// GY tariff prices for normal case 
		let priceONDisc = solarCalc.calculateUnitCost(priceObj.priceON, data.discountON, data.currency);
		let priceOFFDisc = solarCalc.calculateUnitCost(priceObj.priceOFF, data.discountOFF, data.currency);
		let priceSTDDisc = solarCalc.calculateUnitCost(priceObj.priceSTD ?? 0, data.discountSTD ?? 0, data.currency);

		// Pricing calculations for normal case
		const normal_prod = {
			ON: prodObj.solarON,
			OFF: prodObj.solarOFF,
			STD: prodObj.solarSTD ?? 0,
			TOTAL: prodObj.solarTotal,
		};
		const normal_unitcost = {
			ON: priceObj.priceON,
			OFF: priceObj.priceOFF,
			STD: priceObj.priceSTD ?? 0,
			FT: data.FTcost,
		};
		const GYdiscount = {
			ON: data.discountON,
			OFF: data.discountOFF,
			STD: data.discountSTD ?? 0,
			FT: data.discountFT,
		};
		let costsObj = solarCalc.calculateProdCost(
			normal_prod,
			normal_unitcost,
			GYdiscount,
			data.currency
		);

		// Savings calculations for normal case
		const savingsObj = solarCalc.calculateSavings(
			normal_prod,
			normal_unitcost,
			GYdiscount,
			data.currency
		);
		const savingsSTD = Math.round((prodObj.solarSTD ?? 0) * (priceObj.priceSTD ?? 0) * (data.discountSTD ?? 0));
		let total_savings = savingsObj.savingsTotal + savingsSTD;

		// normal pricing per unit with calculated production 
		let totalPriceON = costsObj.costON;
		let totalPriceOFF = costsObj.costOFF;
		let totalPriceSTD = costsObj.costSTD ?? 0;

		// Subtotal calculation for normal case
		let subtotalPrice = isSCFIT ? parseNo(costsObj.costTotal + SRSubtotal) : costsObj.costTotal;
		subtotalPrice = extraInvoiceTableRow({ action: 'calSubtotal', params: { subtotal: subtotalPrice } }); // return params.subtotal + specialDiscount (extra line amount) or just subtotal

		// VAT subtotal calculation for normal case
		const normal_VATObj = solarCalc.calculateVAT(subtotalPrice, data.currency, data.vat);
		let VATPrice = normal_VATObj.VAT;

		// Total Due calculation for normal case
		const tempTotal = normal_VATObj.totalDue;
		let totalDue = tempTotal;

		// ------------------- Tariff prices changed case -------------------
		if (data.old_price != null) {
			const row_for_old = data.costObj?.costsObj_prev;
			const row_for_new = data.costObj?.costsObj_curr;

			// GY tariff prices for tariff prices changed case
			priceONDisc = { from: row_for_old.unitcostON, to: row_for_new.unitcostON };
			priceOFFDisc = { from: row_for_old.unitcostOFF, to: row_for_new.unitcostOFF };
			priceSTDDisc = { from: 0, to: priceSTDDisc };

			// Solar production for tariff prices changed case
			prodObj.solarON = { from: row_for_old.prod_on, to: row_for_new.prod_on };
			prodObj.solarOFF = { from: row_for_old.prod_off, to: row_for_new.prod_off };
			prodObj.solarSTD = { from: row_for_old.prod_std ?? 0, to: row_for_new.prod_std ?? 0 };

			// EVN tariff prices for tariff prices changed case 
			priceObj.priceON = { from: data.old_price.price_on, to: data.priceON };
			priceObj.priceOFF = { from: data.old_price.price_off, to: data.priceOFF };

			// Savings calculations for tariff prices changed case
			const savings_on = (priceObj.priceON.from * prodObj.solarON.from * GYdiscount.ON) + (priceObj.priceON.to * prodObj.solarON.to * GYdiscount.ON);
			const savings_off = (priceObj.priceOFF.from * prodObj.solarOFF.from * GYdiscount.OFF) + (priceObj.priceOFF.to * prodObj.solarOFF.to * GYdiscount.OFF);
			const savings_std = (priceObj.priceSTD ?? 0) * (row_for_new.prod_std ?? 0) * GYdiscount.STD;
			total_savings = Math.round((savings_on || 0) + (savings_off || 0) + savings_std);

			// totalPriceON, totalPriceOFF, totalPriceSTD for tariff prices changed case
			totalPriceON = { from: row_for_old.costON, to: row_for_new.costON };
			totalPriceOFF = { from: row_for_old.costOFF, to: row_for_new.costOFF };
			totalPriceSTD = { from: row_for_old.costSTD ?? 0, to: row_for_new.costSTD ?? 0 };

			// Subtotal calculation for tariff prices changed case
			subtotalPrice = isSCFIT ? parseNo((row_for_new.costTotal || 0) + (row_for_old.costTotal || 0) + SRSubtotal) : parseNo((row_for_new.costTotal || 0) + (row_for_old.costTotal || 0));
			subtotalPrice = extraInvoiceTableRow({ action: 'calSubtotal', params: { subtotal: subtotalPrice } });

			// VAT subtotal calculation for tariff prices changed case
			const changed_VATObj = solarCalc.calculateVAT(subtotalPrice, data.currency, data.vat);
			VATPrice = changed_VATObj.VAT;

			// Total Due calculation for tariff prices changed case
			totalDue = changed_VATObj.totalDue;
		}

		// Location section 
		const locationSection = buildLocationSection(location);

		// Location section
		const entitySection = buildEntitySection(solarEntity);

		const paymentSection = hasBankAcc
			? buildPaymentSection(solarEntity.name, bankAccount)
			: [""];

		const doc_table_body = [
			buildHeaderBodySection(),
			[
				{
					text: getTableHeader({ dateStr: data.dateStr }),
					style: "table_main_rows",
					bold: true,
					colSpan: 4,
				},
			],
			buildExtraLinesSection(data.specialComment, data.specialDiscount || 0),
			isSCFIT && SRParams.sharedPercent > 0
				? buildShareRevenueSection(SRParams)
				: ["", "", "", ""],
			[
				{
					colSpan: 3,
					text: "Subtotal / Tổng cộng",
					bold: true,
					alignment: "right",
					style: "table_main_rows",
				},
				"",
				"",
				{
					text: formatNumber(subtotalPrice, 0),
					bold: true,
					alignment: "right",
					style: "table_main_rows",
				},
			], // Subtotal
			[
				{
					colSpan: 3,
					text: `VAT ${vatDisplay}%`,
					bold: true,
					alignment: "right",
					style: ["table_main_rows", { lineHeight: 0.9 }],
				},
				"",
				"",
				{
					text: formatNumber(VATPrice, 0),
					bold: true,
					alignment: "right",
					style: ["table_main_rows", { lineHeight: 0.9 }],
				},
			], // VAT
			[
				{
					colSpan: 3,
					text: "TOTAL DUE / TỔNG TIỀN CHƯA THANH TOÁN (VND)",
					bold: true,
					alignment: "right",
					border: [false, true, false, true],
					style: { lineHeight: 1 },
				},
				{ text: "", border: [false, true, false, true] },
				{ text: "", border: [false, true, false, true] },
				{
					text: formatNumber(totalDue, 0),
					bold: true,
					alignment: "right",
					border: [false, true, false, true],
					style: { lineHeight: 1 },
				},
			], // Total Due
		];

		const detailed_param = {
			itemDesc, discountOnText, discountOffText, discountStdText,
			solarON: prodObj.solarON, solarOFF: prodObj.solarOFF, solarSTD: prodObj.solarSTD,
			priceONDisc, priceOFFDisc, priceSTDDisc,
			totalPriceON, totalPriceOFF, totalPriceSTD,
			showSTDRow,
		};

		// detailed product section and pricing calculation section
		const mainRows = !Array.isArray(itemDesc) ? sectionWithFullPeriod(detailed_param) : sectionWithPartialPeriod(detailed_param);
		doc_table_body.splice(2, 0, ...mainRows);

		const docDefinition = {
			pageSize: "A4",
			content: [
				{
					nodeName: "DIV",
					columns: [
						{
							nodeName: "DIV",
							width: "50%",
							stack: [
								{
									image: logo,
									width: 150,
								},
								...entitySection,
								{
									text: "TO",
									style: ["boldHeader"],
								},
								{
									nodeName: "DIV",
									margin: [20, 10],
									stack: [
										{
											text: billingName,
											style: ["boldHeader"],
										},
										{
											text: billingAddress,
										},
										{
											text: `TAX ID : ${data.taxID}`,
										},
									],
								},
							],
						},
						{
							nodeName: "DIV",
							width: "50%",
							stack: [
								// second column consists of paragraphs
								{
									text: "Proforma Invoice",
									style: "header_lg",
								},
								{
									margin: [35, 0, 0, 0],
									table: {
										body: [
											["", ""],
											["", ""],
											[
												{
													text: "DOCUMENT NO.",
													style: ["invoice_details", { bold: true }],
												},
												{
													text: invoiceNumber,
													style: ["invoice_details"],
												},
											],
											[
												{
													text: "DATE",
													style: ["invoice_details", { bold: true }],
												},
												{
													text: moment(data.dateStr).endOf("month").format("DD/MM/YYYY"),
													style: ["invoice_details"],
												},
											],
											[
												{
													text: "PROJECT ID",
													style: ["invoice_details", { bold: true }],
												},
												{ text: data.projectID, style: ["invoice_details"] },
											],
											[
												{
													text: "Credit Term",
													style: ["invoice_details", { bold: true }],
												},
												{
													text: `${creditTerm} days`,
													style: ["invoice_details"],
												},
											],
											[
												{
													text: "Due date",
													style: ["invoice_details", { bold: true }],
												},
												{ text: dueDate, style: ["invoice_details"] },
											],
											locationSection[0],
											locationSection[1],
										],
									},
									layout: {
										defaultBorder: false,
									},
								},
							],
						},
					],
				},
				{
					style: "table_main",
					table: {
						body: doc_table_body,
						widths: ["55%", "15%", "15%", "15%"],
					},
					layout: {
						defaultBorder: false,
						paddingLeft: function (i, node) {
							return 0;
						},
						paddingRight: function (i, node) {
							return 0;
						},
						paddingTop: function (i, node) {
							return 0;
						},
						paddingBottom: function (i, node) {
							return 0;
						},
					},
				},
				{
					columns: buildTariffHoursSection(
						data.tariffData.tariffID,
						tariff,
						tariffCategory
					),
					columnGap: 20,
				},
				{
					columns: buildTariffPriceInfo(
						data.tariffData.tariffID,
						evnText,
						priceObj,
						{ priceONDisc: priceONDisc, priceOFFDisc: priceOFFDisc, GYDiscountOnText, GYDiscountOffText },
						data.old_price,
						itemDescPeriod
					),
					columnGap: 20,
				},
				total_savings !== 0
					? {
						columns: buildSavings({ dateStr: data.dateStr }, total_savings),
						columnGap: 20,
					}
					: "",
			],
			footer: [
				{
					nodeName: "DIV",
					columns: [
						{
							stack: [
								{
									text: "..............................................................................",
									style: "sub_signature",
								},
								{
									text: `Authorized by ${solarEntity.name}`,
								},
							],
							style: "signature",
						},
						{
							stack: [
								{
									text: "..............................................................................",
									style: "sub_signature",
								},
								{
									text: "Received by: ___________________",
									style: "sub_signature",
								},
								{ text: "Date ....../....../......", style: "sub_signature" },
							],
							style: "signature",
						},
					],
					margin: [40, -135, 40, 0],
				},
				{
					stack: paymentSection,
					margin: [40, 20, 0, 0],
				},
				[
					{
						text: "Please ensure that Payment Request No., Date and Customer ID No. are clearly stated on the Description of any Bank Transfers. All bank charges should be rendered by your end.",
						style: "boldHeader",
						margin: [40, 10, 40, 0],
					},
					{
						text: "Vui lòng ghi đầy đủ số Phiếu yêu cầu thanh toán, Ngày và Mã Khách hàng khi chuyển khoản.\nMọi khoản phí ngân hàng sẽ do khách hàng chịu.",
						italics: true,
						margin: [40, 0, 40, 0],
					},
				],
			],
			styles: {
				header_lg: {
					fontSize: 14,
					margin: [39, 0, 0, 0],
				},
				sub_header: {
					margin: [0, 5, 0, 0],
					decoration: "underline",
				},
				boldHeader: { bold: true },
				table_main: {
					margin: [0, 5, 0, 0],
				},
				footer: {
					fontSize: 7,
				},
				savings: {
					color: "green",
					bold: true,
				},
				signature: {
					alignment: "center",
				},
				GYtariff: {
					fontSize: 6,
				},
			},
			defaultStyle: {
				font: "OpenSans",
				fontSize: 7,
			},
		};

		onDocDefinition(clone(docDefinition));

		// Generate PDFMAKE document
		const pdfDocGenerator = pdfMake.createPdf(docDefinition);

		// Set PDFMAKE config for download in the later stage
		setPDFDoc(docDefinition);

		if (PDF == null && selectedSite != null) {
			pdfDocGenerator.getDataUrl((dataUrl) => {
				setPDF(dataUrl);
			});
		}
	};

	/**
	 * Build location object
	 * @param {location string array} location
	 * @returns location object
	 */
	const buildLocationSection = (location) => {
		if (location != "")
			return [
				[
					{
						text: "Location:",
						style: ["invoice_details", { bold: true }],
					},
					{ text: location[0], style: ["invoice_details"] },
				],
				[
					{
						colSpan: 2,
						text: location[1],
						style: ["invoice_details"],
					},
					"",
				],
			];
		else
			return [
				[
					{
						text: "",
					},
					{ text: "" },
				],
				[
					{
						colSpan: 2,
						text: "",
					},
					"",
				],
			];
	};

	/**
	 * Build entity section object
	 * @param {Solar entity object} solarEntity
	 * @returns Entity section object
	 */
	const buildEntitySection = (solarEntity) => {
		const addressArr = solarEntity.address.split("_");
		const addressRows = addressArr.map((rows) => ({ text: rows }));

		const section = [
			{
				text: solarEntity.name,
				style: ["boldHeader"],
			},
			...addressRows,
			{
				text: `TAX ID : ${solarEntity.taxID}`,
			},
			{
				text: `Tel: ${solarEntity.telephone}`,
			},
		];

		return section;
	};

	/**
	 * Build savings info
	 * @param {Solar entity object} solarEntity
	 * @returns Entity section object
	 */
	const buildSavings = (dateObj, savings) => {
		const lang = ["en", "vi"];
		let columns = [];

		const month = {
			en: moment(dateObj.dateStr, "MMM-YYYY")
				.endOf("month")
				.format("MMMM-YYYY"),
			vi: moment(dateObj.dateStr, "MMM-YYYY").endOf("month").format("MM-YYYY"),
		};
		lang.map((v) => {
			let savingsSection = [
				{
					text:
						v === "en"
							? `Savings generated compared to Grid In ${month.en} (VND)`
							: `Số tiền tiết kiệm so với sử dụng điện lưới quốc gia tháng ${month.vi} (VND)`,
					margin: [0, 5, 0, 0],
					style: ["sub_header", "savings"],
				},
				{ text: formatNumber(savings, 0), style: "savings" },
			];
			columns.push({ stack: savingsSection });
		});

		return columns;
	};

	/**
	 * Build tariff hours section
	 * @param {string} contractID
	 * @param {string} tariff
	 * @returns Tariff hours section
	 */
	const buildTariffHoursSection = (tariff_id, tariff, tariffCategory) => {
		let lang = ["en", "vi"];
		let columns = [];
		lang.map((v) => {
			let tariffHoursSection = [
				{
					text: v === "en" ? "About Proforma Invoice" : "Về Bảng tính chi tiết",
					margin: [0, 5, 0, 1],
					style: { fontSize: 15, italics: true, bold: true },
				},
			];

			// Not an FIT site
			if (tariff_id !== "FIT") {
				tariffHoursSection = [
					...tariffHoursSection,
					{
						text:
							v === "en"
								? "Based on the following electricity tariff"
								: "Dựa trên biểu giá điện bán lẻ",
						margin: [0, 0, 0, 1],
						style: "boldHeader",
					},
					{
						text:
							v === "en"
								? `EVN Tariff for ${tariffCategory}`
								: `Điện trong lĩnh vực ${tariffCategory}`,
						margin: [0, 0, 0, 1],
						style: "GYtariff",
					},
					{ text: tariff, margin: [0, 0, 0, 1], style: "GYtariff" },
				];
			}

			tariffHoursSection = [
				...tariffHoursSection,
				{
					text:
						v === "en"
							? `Definition of ${peakLabels[0]}`
							: `Định nghĩa Giờ cao điểm`,
					style: ["sub_header", "boldHeader"],
				},
				{
					text:
						v === "en"
							? "Monday - Saturday; Morning: 09:30 to 11:30, Evening: 17:00 to 20:00"
							: "Thứ Hai - Thứ Bảy; Buổi sáng: 09:30 đến 11:30, Buổi tối: 17:00 đến 20:00",
					style: "GYtariff",
				},
				{
					text:
						v === "en"
							? `Definition of ${peakLabels[1]}`
							: `Định nghĩa Giờ bình thường`,
					style: ["sub_header", "boldHeader"],
				},
				{
					text:
						v === "en"
							? "Monday - Saturday; Morning: 04:00 to 09:30, Afternoon: 11:30 to 17:00, Evening: 20:00 to 22:00"
							: "Thứ Hai - Thứ Bảy; Buổi sáng: 04:00 đến 09:30, Buổi chiều: 11:30 đến 17:00, Buổi tối: 20:00 đến 22:00",
					style: "GYtariff",
				},
				{
					text:
						v === "en"
							? "Sunday and Day-off: 04:00 to 22:00"
							: "Chủ Nhật và ngày nghỉ: 04:00 đến 22:00",
					style: "GYtariff",
				},
			];

			columns.push({ stack: tariffHoursSection });
		});

		return columns;
	};

	/**
	 * Build tariff price section
	 * @param {string} contractID
	 * @param {string} evnText
	 * @param {obj} priceObj
	 * @param {obj} discountObj
	 * @returns Tariff price section
	 */
	const buildTariffPriceInfo = (tariff_id, evnText, priceObj, discountObj, old_price, periods) => {
		let lang = ["en", "vi"];
		let columns = [];
		lang.map((v) => {
			let col = [];
			// Site is 100% FIT
			if (tariff_id === "FIT") {
				col = [
					{
						text: v === "en" ? `Feed-in tariff` : "Giá bán điện năng",
						style: ["sub_header", "boldHeader"],
					},
					{
						text:
							v === "en"
								? `${peakLabels[0]} (VND/kWh) = ${formatNumber(
									priceObj.priceON
								)}`
								: `Giờ cao điểm (VND/kWh) = ${formatNumber(priceObj.priceON)}`,
						margin: [0, 1, 0, 0],
					},
					{
						text:
							v === "en"
								? `${peakLabels[1]} (VND/kWh) = ${formatNumber(
									priceObj.priceOFF
								)}`
								: `Giờ bình thường (VND/kWh) = ${formatNumber(
									priceObj.priceOFF
								)}`,
						margin: [0, 1, 0, 0],
					},
				];
			}
			// Site is not 100% FIT
			else {
				const discountPriceOn = discountObj.priceONDisc;
				const discountPriceOff = discountObj.priceOFFDisc;
				const discountOn = discountObj.GYDiscountOnText;
				const discountOff = discountObj.GYDiscountOffText;

				if (old_price != null && periods != null) {
					col = [
						{
							text:
								v === "en"
									? `${evnText.en} tariff (${periods.prev.from} - ${periods.prev.to})`
									: `Biểu giá điện lưới quốc gia ${evnText.vi} (${periods.prev.from} - ${periods.prev.to})`,
							style: ["sub_header", "boldHeader"],
						},
						{
							text:
								v === "en"
									? `${peakLabels[0]} (VND/kWh) = ${formatNumber(priceObj.priceON?.from)}`
									: `Giờ cao điểm (VND/kWh) = ${formatNumber(priceObj.priceON?.from)}`,
							style: "GYtariff",
						},
						{
							text:
								v === "en"
									? `${peakLabels[1]} (VND/kWh) = ${formatNumber(priceObj.priceOFF?.from)}`
									: `Giờ bình thường (VND/kWh) = ${formatNumber(priceObj.priceOFF?.from)}`,
							style: "GYtariff",
						},
						{
							text:
								v === "en"
									? `${evnText.en} tariff (${periods.prev.from} - ${periods.prev.to})`
									: `Biểu giá điện lưới quốc gia ${evnText.vi} (${periods.curr.from} - ${periods.curr.to})`,
							style: ["sub_header", "boldHeader"],
						},
						{
							text:
								v === "en"
									? `${peakLabels[0]} (VND/kWh) = ${formatNumber(priceObj.priceON?.to)}`
									: `Giờ cao điểm (VND/kWh) = ${formatNumber(priceObj.priceON?.to)}`,
							style: "GYtariff",
						},
						{
							text:
								v === "en"
									? `${peakLabels[1]} (VND/kWh) = ${formatNumber(priceObj.priceOFF?.to)}`
									: `Giờ bình thường (VND/kWh) = ${formatNumber(priceObj.priceOFF?.to)}`,
							style: "GYtariff",
						},
						{
							text:
								v === "en"
									? `GreenYellow Tariff (${periods.prev.from} - ${periods.prev.to})`
									: `Biểu giá điện của GreenYellow (${periods.prev.from} - ${periods.prev.to})`,
							style: ["sub_header", "boldHeader"],
						},
						{
							text:
								v === "en"
									? `${peakLabels[0]} (VND/kWh) = ${formatNumber(discountPriceOn?.from, 2)} ${discountOn.en}`
									: `Giờ cao điểm (VND/kWh) = ${formatNumber(discountPriceOn?.from, 2)} ${discountOn.vi}`,
							style: "GYtariff",
						},
						{
							text:
								v === "en"
									? `${peakLabels[1]} (VND/kWh) = ${formatNumber(discountPriceOff?.from, 2)} ${discountOff.en}`
									: `Giờ bình thường (VND/kWh) = ${formatNumber(discountPriceOff?.from, 2)} ${discountOff.vi}`,
							style: "GYtariff",
						},
						{
							text:
								v === "en"
									? `GreenYellow Tariff (${periods.curr.from} - ${periods.curr.to})`
									: `Biểu giá điện của GreenYellow (${periods.curr.from} - ${periods.curr.to})`,
							style: ["sub_header", "boldHeader"],
						},
						{
							text:
								v === "en"
									? `${peakLabels[0]} (VND/kWh) = ${formatNumber(discountPriceOn?.to, 2)} ${discountOn.en}`
									: `Giờ cao điểm (VND/kWh) = ${formatNumber(discountPriceOn?.to, 2)} ${discountOn.vi}`,
							style: "GYtariff",
						},
						{
							text:
								v === "en"
									? `${peakLabels[1]} (VND/kWh) = ${formatNumber(discountPriceOff?.to, 2)} ${discountOff.en}`
									: `Giờ bình thường (VND/kWh) = ${formatNumber(discountPriceOff?.to, 2)} ${discountOff.vi}`,
							style: "GYtariff",
						},
					];
				} else {
					col = [
						{
							text:
								v === "en"
									? `${evnText.en} tariff`
									: `Biểu giá điện lưới quốc gia hiện tại ${evnText.vi}`,
							style: ["sub_header", "boldHeader"],
						},
						{
							text:
								v === "en"
									? `${peakLabels[0]} (VND/kWh) = ${formatNumber(priceObj.priceON)}`
									: `Giờ cao điểm (VND/kWh) = ${formatNumber(priceObj.priceON)}`,
							style: "GYtariff",
						},
						{
							text:
								v === "en"
									? `${peakLabels[1]} (VND/kWh) = ${formatNumber(priceObj.priceOFF)}`
									: `Giờ bình thường (VND/kWh) = ${formatNumber(priceObj.priceOFF)}`,
							style: "GYtariff",
						},
						{
							text:
								v === "en"
									? "GreenYellow Tariff"
									: "Biểu giá điện của GreenYellow",
							style: ["sub_header", "boldHeader"],
						},
						{
							text:
								v === "en"
									? `${peakLabels[0]} (VND/kWh) = ${formatNumber(discountPriceOn, 2)} ${discountOn.en}`
									: `Giờ cao điểm (VND/kWh) = ${formatNumber(discountPriceOn, 2)} ${discountOn.vi}`,
							style: "GYtariff",
						},
						{
							text:
								v === "en"
									? `${peakLabels[1]} (VND/kWh) = ${formatNumber(discountPriceOff, 2)} ${discountOff.en}`
									: `Giờ bình thường (VND/kWh) = ${formatNumber(discountPriceOff, 2)} ${discountOff.vi}`,
							style: "GYtariff",
						},
					];
				}
			}
			columns.push({ stack: col });
		});

		return columns;
	};

	/**
	 * Build header in body section
	 * @returns header in body section
	 */
	const buildHeaderBodySection = () => {
		return [
			{
				text: [
					{
						text: "DESCRIPTION\n",
						bold: true,
					},
					{
						text: "CHI TIẾT",
						italics: true,
					},
				],
				border: [false, true, false, true],
			},
			{
				text: [
					{
						text: "QUANTITY (kWh)\n",
						bold: true,
					},
					{
						text: "SẢN LƯỢNG (kWh)",
						italics: true,
					},
				],
				border: [false, true, false, true],
				alignment: "right",
			},
			{
				text: [
					{
						text: "UNIT COST\n",
						bold: true,
					},
					{
						text: "ĐƠN GIÁ",
						italics: true,
					},
				],
				border: [false, true, false, true],
				alignment: "right",
			},
			{
				text: [
					{
						text: "TOTAL\n",
						bold: true,
					},
					{
						text: "THÀNH TIỀN (VND)",
						italics: true,
					},
				],
				border: [false, true, false, true],
				alignment: "right",
			},
		];
	};

	const buildExtraLinesSection = (description, amount) => {
		if (description === null || amount === null) return ["", "", "", ""];
		const descriptions = description.split(" / ");
		const descriptionEN = descriptions[0];
		const descriptionVI = descriptions[1];
		return [
			{
				text: [
					{
						text: `${descriptionEN} \n`,
					},
					{
						text: `${descriptionVI} `,
						italics: true,
						style: { lineHeight: 1.6 },
					},
				],
				style: "table_main_rows",
			}, "", "",
			{
				text: formatNumber(amount, 0),
				style: "table_main_rows",
				bold: true,
				alignment: "right",
			}
		]
	}

	/**
	 * Build shared revenue section
	 * @param {obj} param
	 * @returns Shared revenue section
	 */
	const buildShareRevenueSection = (param) => {
		const percentText = parseNo(param.sharedPercent * 100);
		return [
			{
				text: [
					{
						text: `Sharing revenue of energy delivered to grid (${percentText}% of kWh injected)\n`,
					},
					{
						text: `Lợi nhuận chia sẻ từ doanh thu bán điện lên lưới điện quốc gia (${percentText}%)`,
						italics: true,
						style: { lineHeight: 1.6 },
					},
				],
				style: "table_main_rows",
			},
			{
				text: `${formatNumber(param.evnTotal, 3)}`,
				alignment: "right",
				style: "table_main_rows",
				padding: 0,
			},
			{
				text: `${formatNumber(param.unitCost, 2)}`,
				alignment: "right",
				style: "table_main_rows",
			},
			{
				text: `${formatNumber(param.subtotal)}`,
				bold: true,
				alignment: "right",
				style: "table_main_rows",
			},
		];
	};

	/**
	 * Build payment section object
	 * @param {Bank account obj} bankAccount
	 * @returns Payment section obj
	 */
	const buildPaymentSection = (entityName, bankAccount) => {
		let accountNumbers = bankAccount.accNumber.split(",");
		let accountRemarks = bankAccount.accNumRemark.split(",");

		let accNumSection = [];

		if (accountNumbers.length === accountRemarks.length) {
			accountNumbers = accountNumbers.map((str) => str.trim());
			accountRemarks = accountRemarks.map((str) => str.trim());

			accNumSection.push("Account number");
			accNumSection.push({
				text: "/ Số tài khoản: ",
				italics: true,
			});

			for (let i = 0; i < accountNumbers.length; i++) {
				const accNumberStr = { text: accountNumbers[i], style: "boldHeader" };
				const accRemarkStr = { text: ` [${accountRemarks[i]}]  ` };

				accNumSection.push(accNumberStr);
				accNumSection.push(accRemarkStr);
			}
		}

		const section = [
			{
				text: [
					"Please make the payment by remittance to",
					{
						text: "/ Vui lòng thanh toán cho ",
						italics: true,
					},
					{
						text: entityName,
						style: "boldHeader",
					},
				],
			},
			{
				text: [
					"Account Name",
					{
						text: "/ Tên tài khoản: ",
						italics: true,
					},
					{
						text: bankAccount.accName,
						style: "boldHeader",
					},
				],
			},
			{
				text: accNumSection,
			},
			{
				text: [
					"Bank Name",
					{
						text: "/ Tên Ngân hàng: ",
						italics: true,
					},
					{ text: `${bankAccount.bankName}, `, style: "boldHeader" },
					"Branch",
					{
						text: "/ Chi nhánh: ",
						italics: true,
					},
					{ text: bankAccount.branch, style: "boldHeader" },
				],
			},

			{
				text: [
					"Bank Swift Code",
					{
						text: "/ Mã Swift: ",
						italics: true,
					},
					{ text: bankAccount.swiftCode, style: "boldHeader" },
				],
			},
		];

		return section;
	};

	/**
	 * Get discount text
	 */
	const getDiscountText = (discount) => {
		return {
			en: discount > 0 ? `- Discount ${parseFloat(formatNumber(discount * 100, 2))}%` : "",
			vi:
				discount > 0 ? `- Chiết khấu ${parseFloat(formatNumber(discount * 100, 2))}%` : "",
		};
	};

	/**
	 * Get discount text
	 */
	const getGYDiscountText = (discount) => {
		return {
			en:
				discount > 0
					? `(${parseFloat(formatNumber(discount * 100, 2))}% discount from EVN's Tariff)`
					: "",
			vi:
				discount > 0
					? `(bao gồm ${parseFloat(formatNumber(
						discount * 100,
						2
					))}% chiết khấu giá điện của EVN)`
					: "",
		};
	};

	/**
	 * Get tariff text
	 * @param {obj} tariffData
	 * @returns Tariff text
	 */
	const getTariffText = (tariffData) => {
		let tariffText = "-";

		if (tariffData != null) {
			const tariffID = tariffData.tariffID;
			const tariffName = tariffID.split("/")[0];
			const tariffCode = tariffName.split(" ")[1];
			const tariffDesc = tariffData.tariffDesc;
			tariffText = `${tariffCode} for ${tariffDesc}`;
		}
		return tariffText;
	};

	/**
	 * Get EVN text
	 * @param {string} EVNStr
	 * @returns EVN text
	 */
	const getEVNText = (EVNStr) => {
		const strArr = EVNStr.split("_");
		return {
			en: `${strArr[0]} (${strArr[1]})`,
			vi: `(${strArr[1]})`,
		};
	};

	/**
	 * Get table item description
	 * @returns Table item description
	 */
	const getItemDesc = (period) => {
		return period == null ? {
			en: [
				`Consumption during ${peakLabels[0]}`,
				`Consumption during ${peakLabels[1]}`,
				`Consumption during ${peakLabels[2]}`,
			],
			vi: [
				`Sản lượng điện trong ${peakLabelsVN[0]}`,
				`Sản lượng điện trong ${peakLabelsVN[1]}`,
				`Sản lượng điện trong ${peakLabelsVN[2]}`,
			],
		} : [
			{
				en: [
					`Consumption during ${peakLabels[0]} from ${period.prev.from} to ${period.prev.to}`,
					`Consumption during ${peakLabels[1]} from ${period.prev.from} to ${period.prev.to}`,
					`Consumption during ${peakLabels[2]} from ${period.prev.from} to ${period.prev.to}`,
				],
				vi: [
					`Sản lượng điện trong ${peakLabelsVN[0]} từ ${period.prev.from} đến ${period.prev.to}`,
					`Sản lượng điện trong ${peakLabelsVN[1]} từ ${period.prev.from} đến ${period.prev.to}`,
					`Sản lượng điện trong ${peakLabelsVN[2]} từ ${period.prev.from} đến ${period.prev.to}`,
				],
			},
			{
				en: [
					`Consumption during ${peakLabels[0]} from ${period.curr.from} to ${period.curr.to}`,
					`Consumption during ${peakLabels[1]} from ${period.curr.from} to ${period.curr.to}`,
					`Consumption during ${peakLabels[2]} from ${period.curr.from} to ${period.curr.to}`,
				],
				vi: [
					`Sản lượng điện trong ${peakLabelsVN[0]} từ ${period.curr.from} đến ${period.curr.to}`,
					`Sản lượng điện trong ${peakLabelsVN[1]} từ ${period.curr.from} đến ${period.curr.to}`,
					`Sản lượng điện trong ${peakLabelsVN[2]} từ ${period.curr.from} đến ${period.curr.to}`,
				],
			}
		];
	};

	/**
	 * Get price object based on contract ID
	 * @param {obj} data
	 * @returns price object
	 */
	const getPriceObj = (data) =>
		data.tariffData.tariffID === "FIT"
			? { priceON: data.FITOn, priceOFF: data.FITOff, priceSTD: data.FITStd ?? 0 }
			: { priceON: data.priceON, priceOFF: data.priceOFF, priceSTD: data.priceSTD ?? 0 };

	/**
	 * Get table header row based on contract ID
	 * @param {obj} dateObj
	 * @returns Table header row
	 */
	const getTableHeader = (dateObj) => {
		const month = {
			en: dateObj.dateStr,
			vi: moment(dateObj.dateStr, "MMM-YYYY").endOf("month").format("MM-YYYY"),
		};
		return `${month.en} - Monthly Electric Consumption / T${month.vi} - Sản lượng điện tiêu thụ hàng tháng`;
	};

	const sectionWithFullPeriod = (param) => {
		const { itemDesc, discountOnText, discountOffText, discountStdText, solarON, solarOFF, solarSTD, priceONDisc, priceOFFDisc, priceSTDDisc, totalPriceON, totalPriceOFF, totalPriceSTD, showSTDRow } = param;
		const on_peak_row = [
			{
				text: [
					{
						text: `${itemDesc.en[0]} ${discountOnText.en}\n`,
					},
					{
						text: `${itemDesc.vi[0]} ${discountOnText.vi}`,
						italics: true,
						style: { lineHeight: 1.6 },
					},
				],
				style: "table_main_rows",
			},
			{
				text: `${formatNumber(solarON, 3)}`,
				style: "table_main_rows",
				alignment: "right",
			},
			{
				text: formatNumber(priceONDisc, 2),
				style: "table_main_rows",
				alignment: "right",
			},
			{
				text: formatNumber(totalPriceON, 0),
				style: "table_main_rows",
				bold: true,
				alignment: "right",
			},
		];

		const off_peak_row = [
			{
				text: [
					{
						text: `${itemDesc.en[1]} ${discountOffText.en}\n`,
					},
					{
						text: `${itemDesc.vi[1]} ${discountOffText.vi}`,
						italics: true,
						style: { lineHeight: 1.6 },
					},
				],
				style: "table_main_rows",
			},
			{
				text: `${formatNumber(solarOFF, 3)}`,
				style: "table_main_rows",
				alignment: "right",
			},
			{
				text: formatNumber(priceOFFDisc, 2),
				style: "table_main_rows",
				alignment: "right",
			},
			{
				text: formatNumber(totalPriceOFF, 0),
				style: "table_main_rows",
				bold: true,
				alignment: "right",
			},
		];

		const std_row = [
			{
				text: [
					{
						text: `${itemDesc.en[2]} ${discountStdText.en}\n`,
					},
					{
						text: `${itemDesc.vi[2]} ${discountStdText.vi}`,
						italics: true,
						style: { lineHeight: 1.6 },
					},
				],
				style: "table_main_rows",
			},
			{
				text: `${formatNumber(solarSTD, 3)}`,
				style: "table_main_rows",
				alignment: "right",
			},
			{
				text: formatNumber(priceSTDDisc, 2),
				style: "table_main_rows",
				alignment: "right",
			},
			{
				text: formatNumber(totalPriceSTD, 0),
				style: "table_main_rows",
				bold: true,
				alignment: "right",
			},
		];

		return showSTDRow ? [off_peak_row, std_row, on_peak_row] : [off_peak_row, on_peak_row];
	}

	const sectionWithPartialPeriod = (param) => {
		const { itemDesc, discountOnText, discountOffText, discountStdText, solarON, solarOFF, solarSTD, priceONDisc, priceOFFDisc, priceSTDDisc, totalPriceON, totalPriceOFF, totalPriceSTD, showSTDRow } = param;

		const first_period_on_peak_row = [
			{
				text: [
					{
						text: `${itemDesc[0].en[0]} ${discountOnText.en}\n`,
					},
					{
						text: `${itemDesc[0].vi[0]} ${discountOnText.vi}`,
						italics: true,
						style: { lineHeight: 1.6 },
					},
				],
				style: "table_main_rows",
			},
			{
				text: `${formatNumber(solarON.from, 3)}`,
				style: "table_main_rows",
				alignment: "right",
			},
			{
				text: formatNumber(priceONDisc.from, 2),
				style: "table_main_rows",
				alignment: "right",
			},
			{
				text: formatNumber(totalPriceON.from, 0),
				style: "table_main_rows",
				bold: true,
				alignment: "right",
			},
		];
		const first_period_off_peak_row = [{
			text: [
				{
					text: `${itemDesc[0].en[1]} ${discountOffText.en}\n`,
				},
				{
					text: `${itemDesc[0].vi[1]} ${discountOffText.vi}`,
					italics: true,
					style: { lineHeight: 1.6 },
				},
			],
			style: "table_main_rows",
		},
		{
			text: `${formatNumber(solarOFF.from, 3)}`,
			style: "table_main_rows",
			alignment: "right",
		},
		{
			text: formatNumber(priceOFFDisc.from, 2),
			style: "table_main_rows",
			alignment: "right",
		},
		{
			text: formatNumber(totalPriceOFF.from, 0),
			style: "table_main_rows",
			bold: true,
			alignment: "right",
		}];

		const first_period_std_peak_row = [{
			text: [
				{
					text: `${itemDesc[0].en[2]} ${discountStdText.en}\n`,
				},
				{
					text: `${itemDesc[0].vi[2]} ${discountStdText.vi}`,
					italics: true,
					style: { lineHeight: 1.6 },
				},
			],
			style: "table_main_rows",
		},
		{
			text: `${formatNumber(solarSTD.from, 3)}`,
			style: "table_main_rows",
			alignment: "right",
		},
		{
			text: formatNumber(priceSTDDisc.from, 2),
			style: "table_main_rows",
			alignment: "right",
		},
		{
			text: formatNumber(totalPriceSTD.from, 0),
			style: "table_main_rows",
			bold: true,
			alignment: "right",
		}];

		const second_period_on_peak_row = [
			{
				text: [
					{
						text: `${itemDesc[1].en[0]} ${discountOnText.en}\n`,
					},
					{
						text: `${itemDesc[1].vi[0]} ${discountOnText.vi}`,
						italics: true,
						style: { lineHeight: 1.6 },
					},
				],
				style: "table_main_rows",
			},
			{
				text: `${formatNumber(solarON.to, 3)}`,
				style: "table_main_rows",
				alignment: "right",
			},
			{
				text: formatNumber(priceONDisc.to, 2),
				style: "table_main_rows",
				alignment: "right",
			},
			{
				text: formatNumber(totalPriceON.to, 0),
				style: "table_main_rows",
				bold: true,
				alignment: "right",
			},
		];
		const second_period_off_peak_row = [
			{
				text: [
					{
						text: `${itemDesc[1].en[1]} ${discountOffText.en}\n`,
					},
					{
						text: `${itemDesc[1].vi[1]} ${discountOffText.vi}`,
						italics: true,
						style: { lineHeight: 1.6 },
					},
				],
				style: "table_main_rows",
			},
			{
				text: `${formatNumber(solarOFF.to, 3)}`,
				style: "table_main_rows",
				alignment: "right",
			},
			{
				text: formatNumber(priceOFFDisc.to, 2),
				style: "table_main_rows",
				alignment: "right",
			},
			{
				text: formatNumber(totalPriceOFF.to, 0),
				style: "table_main_rows",
				bold: true,
				alignment: "right",
			}
		];

		const second_period_std_peak_row = [
			{
				text: [
					{
						text: `${itemDesc[1].en[2]} ${discountStdText.en}\n`,
					},
					{
						text: `${itemDesc[1].vi[2]} ${discountStdText.vi}`,
						italics: true,
						style: { lineHeight: 1.6 },
					},
				],
				style: "table_main_rows",
			},
			{
				text: `${formatNumber(solarSTD.to, 3)}`,
				style: "table_main_rows",
				alignment: "right",
			},
			{
				text: formatNumber(priceSTDDisc.to, 2),
				style: "table_main_rows",
				alignment: "right",
			},
			{
				text: formatNumber(totalPriceSTD.to, 0),
				style: "table_main_rows",
				bold: true,
				alignment: "right",
			},
		];

		if (showSTDRow) {
			return [first_period_on_peak_row, first_period_off_peak_row, first_period_std_peak_row, second_period_on_peak_row, second_period_off_peak_row, second_period_std_peak_row];
		} else {
			return [first_period_on_peak_row, first_period_off_peak_row, second_period_on_peak_row, second_period_off_peak_row];
		}
	}

	/**
	 * Download PDF document
	 * @returns PDF document
	 */
	const downloadPDF = () =>
		pdfMake.createPdf(PDFDoc).download(`${invoiceName}.pdf`);

	useMemo(() => {
		const labelsAreReady = !peakLabels.includes("");
		if (props.selectedSite != null && labelsAreReady) {
			generateInvoice(props.selectedSite);
		}
	}, [props.selectedSite, peakLabels]);

	return PDF != null ? (
		<>
			<iframe style={{ width: "98%", height: "72vh" }} src={PDF} />
			<Row>
				<Button onClick={downloadPDF}>Download</Button>
			</Row>
		</>
	) : (
		<LoaderGraph style={{ height: "72vh" }} />
	);
}

export default InvoiceTemplate_VN;
